import React from 'react';
import {Box, Grid, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import NetworkDashboardShell from '../components/NetworkDashboard.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, cellText, rowsFrom} from '../networkData';
import {NAVY, TEAL, PageHeader, headCell, bodyCell} from '../components/widgets.comp';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';

const BLUE = '#3B6FA0';

/* Slide 38 — Program Deployment. KPIs and the program list table are copied
   verbatim from the deck's own table/shapes — same 4 real programs and
   figures as School Dashboard's identical page, plus a "Countries" column
   here since Network spans multiple countries. "Provider-Created Programs"
   / "Teacher-Created Programs" (renamed from School's "My Programs") show
   the exact same 4 KPI figures twice, per the deck. "Show all deployed +
   created programs." is a yellow annotation (build instruction), not
   on-page copy, so it isn't rendered — the table already shows every row. */
/* Live data (SND-38-01 … 09), in this page's own layout:
     Provider-created  SND.S_12.C_2 Active Programs · C_3 Schools Assigned · C_4 Program Completion
     Teacher-created   SND.S_12.C_6 · C_7 · C_8
     Program list      SND.S_12.C_10 (the card has no Countries column, so it reads "—")
   "Priority Curriculum Coverage" (SND-38-04 / -08) is not drawn — the Data Map marks
   it removed from the datahouse output by D-10. Every column of the program list
   sorts from its header; filters appear once there are enough programs (QA 9 Oct 2026). */
const kpiText = (card, pct) => {
    if (!card) return '—';
    const v = card.kpi_value;
    if (pct && v !== null && v !== '' && Number.isFinite(Number(v))) return `${Math.trunc(Number(v))}%`;
    return cellText(v);
};
const statsFor = (active, schools, completion) => [
    {label: 'Active Programs', value: kpiText(active), sub: 'deployed / created', accent: TEAL},
    {label: 'Schools Assigned', value: kpiText(schools), accent: BLUE},
    {label: 'Program Completion', value: kpiText(completion, true), accent: BLUE},
];

const StatCardAccent = ({label, value, sub, accent}) => (
    <Box sx={{
        display: 'flex', border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '10px',
        backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        overflow: 'hidden', height: '100%',
    }}>
        <Box sx={{width: '5px', flexShrink: 0, backgroundColor: accent}}/>
        <Box sx={{p: 2, flex: 1}}>
            <Typography sx={{
                fontSize: '10.5px', fontWeight: 700, color: '#6A7282', letterSpacing: '0.6px',
                textTransform: 'uppercase', mb: 1,
            }}>
                {label}
            </Typography>
            <Typography sx={{fontSize: '26px', fontWeight: 800, color: NAVY, lineHeight: 1}}>
                {value}
            </Typography>
            {sub && <Typography sx={{fontSize: '11px', color: '#9CA3AF', mt: 0.75}}>{sub}</Typography>}
        </Box>
    </Box>
);

const DeploymentStats = ({stats}) => (
    <Grid container spacing={2.5} sx={{mb: 4}}>
        {stats.map((s) => (
            <Grid item xs={12} sm={6} md={4} key={s.label}>
                <StatCardAccent {...s}/>
            </Grid>
        ))}
    </Grid>
);

const rateColor = (rate) => {
    const num = parseFloat(rate);
    if (isNaN(num)) return '#374151';
    return num >= 75 ? TEAL : '#E8730C';
};

const PROGRAM_COLUMNS = [
    {key: 'program', label: 'Program', type: 'text', align: 'left'},
    {key: 'creator', label: 'Program Creator', type: 'text', align: 'left'},
    {key: 'targetArea', label: 'Target Area', type: 'text', align: 'left'},
    {key: 'countries', label: 'Countries', type: 'number'},
    {key: 'schools', label: 'Schools', type: 'number'},
    {key: 'enrolled', label: 'Students Enrolled', type: 'number'},
    {key: 'completed', label: 'Students Completed', type: 'number'},
    {key: 'rate', label: 'Completion Rate', type: 'percent'},
    {key: 'status', label: 'Status', type: 'band'},
];

const ProgramsTable = ({rows}) => {
    const t = useTableControls(rows, PROGRAM_COLUMNS);
    return (
        <>
            {(t.chips.length > 0 || rows.length >= 5) && <TableToolbar {...t}/>}
            <TableContainer sx={{
                border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflow: 'auto',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}>
                <Table sx={{minWidth: 1000}}>
                    <TableHead>
                        <TableRow>
                            {PROGRAM_COLUMNS.map((c, i) => (
                                <SortFilterHeaderCell key={c.key} {...t.headerProps(c.key)} sx={{...headCell, textAlign: c.align || 'center', ...(i === 0 ? {pl: 2.5} : {})}}/>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={9} sx={{...bodyCell, textAlign: 'center', py: 4, color: '#6A7282'}}>No programs created or deployed yet.</TableCell>
                            </TableRow>
                        )}
                        {rows.length > 0 && t.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={9} sx={{...bodyCell, textAlign: 'center', py: 4, color: '#6A7282'}}>No program matches the filters.</TableCell>
                            </TableRow>
                        )}
                        {t.rows.map((row, i) => (
                            <TableRow key={`${row.program}-${rows.indexOf(row)}`} sx={{backgroundColor: i % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)'}}>
                                <TableCell sx={{...bodyCell, textAlign: 'left', fontWeight: 700, color: NAVY, pl: 2.5}}>{row.program}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.creator}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.targetArea}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.countries}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.schools}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.enrolled}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.completed}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center', fontWeight: 700, color: rateColor(row.rate)}}>{row.rate}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.status}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const PROGRAM_DEPLOYMENT_SECTION_CODE = 'S_12';
const CARD_SECTIONS = [PROGRAM_DEPLOYMENT_SECTION_CODE];

const ProgramDeploymentPage = () => {
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });
    const card = (c) => findCard(dashboardData, PROGRAM_DEPLOYMENT_SECTION_CODE, c);
    const rows = rowsFrom(card('C_10'), {
        program: 'Program', creator: 'Program Creator', targetArea: 'Target Area', countries: 'Countries',
        schools: 'Schools', enrolled: 'Students Enrolled', completed: 'Students Completed',
        rate: 'Completion Rate %', status: 'Status',
    });

    if (loading) {
        return (
            <NetworkDashboardShell>
                <CircularLoader />
            </NetworkDashboardShell>
        );
    }

    if (!dashboardData) {
        return (
            <NetworkDashboardShell>
                <NoDataAvailable />
            </NetworkDashboardShell>
        );
    }

    return (
    <NetworkDashboardShell>
        <PageHeader
            title="Program Deployment"
            subtitle="Are recommended programs actually being created, assigned, completed, and assessed?"
        />

        <Typography sx={{fontSize: '17px', fontWeight: 700, color: NAVY, mb: 1.5}}>
            Provider-Created Programs
        </Typography>
        <DeploymentStats stats={statsFor(card('C_2'), card('C_3'), card('C_4'))}/>

        <Typography sx={{fontSize: '17px', fontWeight: 700, color: NAVY, mb: 1.5}}>
            Teacher-Created Programs
        </Typography>
        <DeploymentStats stats={statsFor(card('C_6'), card('C_7'), card('C_8'))}/>

        <Typography sx={{fontSize: '17px', fontWeight: 700, color: NAVY, mb: 1.5}}>
            List of Total Programs and Deployment Status
        </Typography>
        <ProgramsTable rows={rows}/>
    </NetworkDashboardShell>
    );
};

export default ProgramDeploymentPage;
